import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

export function SponsorsBanner({ pixelsPerSecond = 40 }) {
  const [logos, setLogos] = useState([]);
  const [rows, setRows] = useState(3);
  const [isVisible, setIsVisible] = useState(false);
  const rowRefs = useRef([]);
  const sectionRef = useRef(null);

  // Intersection Observer to detect when section is near viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect(); // Stop observing once visible
          }
        });
      },
      {
        rootMargin: '200px', // Start loading 200px before the section enters viewport
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Only fetch sponsors data when section becomes visible
  useEffect(() => {
    if (!isVisible) return;

    const fetchSponsors = async () => {
      try {
        const res = await axios.get("/api/public/sponsors");
        const content = res.data.content || [];
        // Handle both array and object format
        if (Array.isArray(content)) {
          setLogos(content);
          setRows(3); // default
        } else {
          setLogos(content.logos || []);
          setRows(content.rows || 3);
        }
      } catch (err) {
        console.error("Sponsor fetch error:", err);
      }
    };

    fetchSponsors();
  }, [isVisible]);

  const logoRows = useMemo(() => {
    const arr = Array.from({ length: rows }, () => []);
    logos.forEach((logo, i) => {
      arr[i % rows].push(logo);
    });
    return arr;
  }, [logos, rows]);

  useEffect(() => {
    rowRefs.current.forEach(track => {
      if (!track) return;
      const fullWidth = track.scrollWidth / 2; // because we duplicate once
      const duration = fullWidth / pixelsPerSecond;
      track.style.animationDuration = `${duration}s`;
    });
  }, [logoRows, pixelsPerSecond]);

  // Don't render anything until data is loaded
  if (!logos.length) return <div ref={sectionRef} style={{ minHeight: '200px' }} />;

  return (
    <>
      <style>
        {`
          @keyframes marquee {
            from { transform: translateX(0); }
            to   { transform: translateX(-50%); }
          }

          .marquee-wrapper {
            width: 100%;
            overflow: hidden;
          }

          .marquee-track {
            display: flex;
            width: max-content;
            animation-name: marquee;
            animation-timing-function: linear;
            animation-iteration-count: infinite;
            animation-direction: normal;
            will-change: transform;
          }

          .logo-item {
            flex: 0 0 auto;
            width: 200px;
            padding: 0 30px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .logo-item img {
            max-width: 140px;
            max-height: 70px;
            object-fit: contain;
            transition: transform 0.3s ease;
          }

          .logo-item a:hover img {
            transform: scale(1.1);
          }
        `}
      </style>

      <section ref={sectionRef} className="mt-5 pb-5" style={{ overflow: "hidden" }}>
        <div className="container">
          <h4
            className="text-center mb-4"
            style={{
              color: "#c90551",
              fontSize: "1.2rem",
              //   textTransform: "uppercase",
              letterSpacing: "2px",
              fontWeight: "600"
            }}
          >
            Proudly Sponsored By
          </h4>

          {logoRows.map((row, rowIndex) => (
            <div key={rowIndex} className="marquee-wrapper" style={{ marginBottom: 22 }}>
              <div
                ref={el => (rowRefs.current[rowIndex] = el)}
                className="marquee-track"
              >
                {[...row, ...row].map((sponsor, i) => (
                  <div key={i} className="logo-item">
                    {sponsor.url ? (
                      <a href={sponsor.url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={sponsor.logo}
                          alt={sponsor.name}
                          loading="lazy"
                          decoding="async"
                        />
                      </a>
                    ) : (
                      <img
                        src={sponsor.logo}
                        alt={sponsor.name}
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="text-center mt-4">
            <a
              href="/donate"
              style={{
                color: "#c90551",
                textDecoration: "none",
                fontWeight: "500"
              }}
              onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
              onMouseLeave={(e) => e.target.style.textDecoration = "none"}
            >
              Become a Sponsor
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
